import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export type AuditLogTimeRange =
  | "today"
  | "last-7-days"
  | "last-30-days"
  | "last-90-days"
  | "all-time";

export interface AuditLogTimeWindow {
  endTime?: string;
  startTime?: string;
}

export const DEFAULT_AUDIT_LOG_TIME_RANGE: AuditLogTimeRange = "last-7-days";

const RANGE_DAY_COUNTS: Partial<Record<AuditLogTimeRange, number>> = {
  today: 1,
  "last-7-days": 7,
  "last-30-days": 30,
  "last-90-days": 90,
};

export const resolveAuditLogTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const getAuditLogTimeWindow = (
  range: AuditLogTimeRange,
  now: Date = new Date(),
  timeZone = resolveAuditLogTimeZone(),
): AuditLogTimeWindow => {
  if (range === "all-time") return {};

  const currentTime = dayjs(now);
  const dayCount = RANGE_DAY_COUNTS[range];
  if (!currentTime.isValid() || !dayCount) return {};

  const startTime = currentTime
    .tz(timeZone)
    .startOf("day")
    .subtract(dayCount - 1, "day")
    .utc();

  return {
    startTime: startTime.toISOString(),
    endTime: currentTime.utc().toISOString(),
  };
};

export const isAuditTimestampWithinTimeRange = (
  timestamp: string | null | undefined,
  range: AuditLogTimeRange,
  now: Date = new Date(),
  timeZone = resolveAuditLogTimeZone(),
): boolean => {
  if (range === "all-time") return true;
  if (!timestamp) return false;

  const timestampValue = new Date(timestamp).getTime();
  if (Number.isNaN(timestampValue)) return false;

  const window = getAuditLogTimeWindow(range, now, timeZone);
  const startValue = window.startTime
    ? new Date(window.startTime).getTime()
    : Number.NEGATIVE_INFINITY;
  const endValue = window.endTime
    ? new Date(window.endTime).getTime()
    : Number.POSITIVE_INFINITY;

  return timestampValue >= startValue && timestampValue <= endValue;
};

export interface AuditRunTimeBounds {
  endedAt?: string | null;
  lastEventAt?: string | null;
  startedAt?: string | null;
}

const validTimestampValues = (timestamps: Array<string | null | undefined>) =>
  timestamps
    .map((timestamp) => (timestamp ? new Date(timestamp).getTime() : Number.NaN))
    .filter((timestamp) => !Number.isNaN(timestamp));

export const doesAuditRunOverlapTimeRange = (
  run: AuditRunTimeBounds,
  range: AuditLogTimeRange,
  now: Date = new Date(),
  timeZone = resolveAuditLogTimeZone(),
): boolean => {
  if (range === "all-time") return true;

  const runTimestamps = validTimestampValues([
    run.startedAt,
    run.endedAt,
    run.lastEventAt,
  ]);
  if (runTimestamps.length === 0) return false;

  const window = getAuditLogTimeWindow(range, now, timeZone);
  const windowStart = window.startTime
    ? new Date(window.startTime).getTime()
    : Number.NEGATIVE_INFINITY;
  const windowEnd = window.endTime
    ? new Date(window.endTime).getTime()
    : Number.POSITIVE_INFINITY;
  const runStart = Math.min(...runTimestamps);
  const runEnd = Math.max(...runTimestamps);

  return runStart <= windowEnd && runEnd >= windowStart;
};
